import { OverridableComponent } from '@material-ui/core/OverridableComponent'
import { darken, makeStyles } from '@material-ui/core/styles'
import SvgIcon, { SvgIconTypeMap } from '@material-ui/core/SvgIcon'
import Typography from '@material-ui/core/Typography'
import clsx from 'clsx'
import React from 'react'
import AddToProjectIcon from 'resources/AddToProjectIcon'

const useIconTextStyles = makeStyles({
    iconWidgetContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    coloredIcon: {
        stroke: (props: WorkspaceActionBarIconTextWidgetStyleProps) =>
            props.iconColor ?? '#FFFFFF',
    },
    disabledIcon: {
        color: (props: WorkspaceActionBarIconTextWidgetStyleProps) =>
            darken(props.iconColor ?? '#FFFF', 0.2),
    },

    loading: {
        '&:before': {
            overflow: 'hidden',
            display: 'inline-block',
            verticalAlign: 'bottom',
            '-webkit-animation': '$ellipsis steps(4,start) 400ms infinite',
            animation: '$ellipsis steps(4,start) 400ms infinite',
            content: '"\\2026"' /* ascii code for the ellipsis character */,
            width: 0,
        },
    },
    '@keyframes ellipsis': {
        to: {
            width: '0.6em',
        },
    },
    clickableIcon: {
        cursor: 'pointer',
    },
})

export interface WorkspaceActionBarIconTextWidgetStyleProps {
    iconColor: string
}

export interface WorkspaceActionBarIconWidgetProps
    extends Partial<WorkspaceActionBarIconTextWidgetStyleProps> {
    icon: OverridableComponent<SvgIconTypeMap> | typeof SvgIcon
    caption?: string
    onIconClick?: VoidFunction
    disabled?: boolean
    hasEllipsis?: boolean
    name?: string
}

const WorkspaceActionBarIconTextWidget: React.FC<WorkspaceActionBarIconWidgetProps> = ({
    icon,
    caption,
    iconColor = '#FFFFFF',
    onIconClick,
    disabled,
    hasEllipsis = false,
    name,
}) => {
    const WidgetIcon = React.useMemo(() => icon, [icon])
    const classes = useIconTextStyles({ iconColor: iconColor ?? '' })

    return (
        <div className={classes.iconWidgetContainer}>
            {WidgetIcon && (
                <WidgetIcon
                    htmlColor={darken(iconColor, disabled ? 0.5 : 0)}
                    className={clsx(
                        onIconClick && !disabled && classes.clickableIcon,
                        icon === AddToProjectIcon && classes.coloredIcon
                    )}
                    onClick={disabled ? undefined : onIconClick}
                    data-testid={`leftWidget-${name}`}
                />
            )}
            <Typography
                component="span"
                variant="caption"
                className={clsx(hasEllipsis && classes.loading)}
            >
                {caption}
            </Typography>
        </div>
    )
}

export default WorkspaceActionBarIconTextWidget
